/**
 * Nomad loading indicator — single source for spinners / page / overlay states.
 * Matches design-reference spinner (Loader2).
 */

import * as React from 'react'
import { Loader2 } from 'lucide-react'
import { cn } from '../utils/cn'

const SIZE_CLASS = {
  sm: 'size-4',
  md: 'size-6',
  lg: 'size-8',
  xl: 'size-12',
} as const

export type LoadingSize = keyof typeof SIZE_CLASS
export type LoadingVariant = 'inline' | 'block' | 'page' | 'overlay'

export interface LoadingProps extends Omit<React.ComponentProps<'svg'>, 'ref'> {
  size?: LoadingSize
  /** inline = icon only; block = section; page = tall page; overlay = fullscreen */
  variant?: LoadingVariant
  tip?: React.ReactNode
  label?: string
}

const Loading = React.forwardRef<SVGSVGElement, LoadingProps>(
  (
    {
      size = 'md',
      variant = 'inline',
      tip,
      label = 'Chargement',
      className,
      ...props
    },
    ref
  ) => {
    const iconSize =
      variant === 'inline' ? size : size === 'md' ? 'lg' : size

    const icon = (
      <Loader2
        ref={ref}
        role={variant === 'inline' ? 'status' : undefined}
        aria-label={variant === 'inline' ? label : undefined}
        aria-hidden={variant !== 'inline' ? true : undefined}
        className={cn(
          'animate-spin text-[var(--primary)]',
          SIZE_CLASS[iconSize],
          variant === 'inline' && className
        )}
        {...props}
      />
    )

    if (variant === 'inline') return icon

    if (variant === 'overlay') {
      return (
        <div
          className={cn(
            'fixed inset-0 z-[1200] flex items-center justify-center',
            'bg-[color-mix(in_oklab,var(--card)_72%,transparent)] backdrop-blur-[2px]',
            className
          )}
          role='status'
          aria-label={label}
        >
          <div className='flex flex-col items-center gap-3'>
            {icon}
            {tip ? (
              <p className='text-sm text-[var(--muted-foreground)]'>{tip}</p>
            ) : null}
          </div>
        </div>
      )
    }

    return (
      <div
        className={cn(
          'flex w-full flex-col items-center justify-center gap-3',
          variant === 'page' ? 'min-h-[60vh]' : 'min-h-[12.5rem] py-12',
          className
        )}
        role='status'
        aria-label={label}
      >
        {icon}
        {tip ? (
          <p className='text-sm text-[var(--muted-foreground)]'>{tip}</p>
        ) : null}
      </div>
    )
  }
)

Loading.displayName = 'Loading'

/** @deprecated Prefer `Loading` — kept as alias for existing kit imports */
const Spinner = Loading

export { Loading, Spinner }
